import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Button,
  Input,
  Textarea,
  Dropdown,
  Option,
  Label,
  Spinner,
  Field,
  Slider,
} from '@fluentui/react-components';
import { ArrowLeftRegular, SaveRegular, LocationRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { PermissionGate, SearchableDropdown } from '../../../components/common';
import { ApiClient, getErrorMessage, newToast } from '../../../configurations/index.config';
import type { Site, Zone, Client } from '../../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '16px',
    borderTop: '1px solid #edebe9',
  },
  gpsSection: {
    display: 'flex',
    gap: '12px',
  },
  infoBox: {
    padding: '12px 16px',
    backgroundColor: '#eff6fc',
    borderLeft: '4px solid #0078d4',
    borderRadius: '4px',
    marginBottom: '16px',
  },
});

const AddCheckpointFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [zones, setZones] = useState<Zone[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [filteredSites, setFilteredSites] = useState<Site[]>([]);

  const [selectedZoneId, setSelectedZoneId] = useState<string>('');
  const [selectedClientId, setSelectedClientId] = useState<string>('');

  const [siteId, setSiteId] = useState<number | undefined>(undefined);
  const [checkpointName, setCheckpointName] = useState('');
  const [description, setDescription] = useState('');
  const [locationDescription, setLocationDescription] = useState('');
  const [gpsLat, setGpsLat] = useState('');
  const [gpsLng, setGpsLng] = useState('');
  const [validationRadius, setValidationRadius] = useState(50);
  const [status, setStatus] = useState<'active' | 'inactive'>('active');

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterSites();
  }, [selectedZoneId, selectedClientId, sites]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [zonesRes, clientsRes, sitesRes] = await Promise.all([
        ApiClient.get('/get-zones'),
        ApiClient.get('/get-clients'),
        ApiClient.get('/get-sites'),
      ]);

      if (zonesRes.data.status === 'success') {
        setZones(zonesRes.data.data || []);
      }
      if (clientsRes.data.status === 'success') {
        setClients(clientsRes.data.data || []);
      }
      if (sitesRes.data.status === 'success') {
        const sitesData = sitesRes.data.data || [];
        setSites(sitesData);
        setFilteredSites(sitesData);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const filterSites = () => {
    let filtered = [...sites];

    if (selectedZoneId) {
      filtered = filtered.filter(site => site.zone_id === parseInt(selectedZoneId));
    }

    if (selectedClientId) {
      filtered = filtered.filter(site => site.client_id === parseInt(selectedClientId));
    }

    setFilteredSites(filtered);

    if (siteId && !filtered.find(s => s.id === siteId)) {
      setSiteId(undefined);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!siteId) {
      newErrors.siteId = 'Site is required';
    }

    if (!checkpointName.trim()) {
      newErrors.checkpointName = 'Checkpoint name is required';
    }

    if (gpsLat && (isNaN(Number(gpsLat)) || Number(gpsLat) < -90 || Number(gpsLat) > 90)) {
      newErrors.gpsLat = 'Invalid latitude (-90 to 90)';
    }

    if (gpsLng && (isNaN(Number(gpsLng)) || Number(gpsLng) < -180 || Number(gpsLng) > 180)) {
      newErrors.gpsLng = 'Invalid longitude (-180 to 180)';
    }

    if (gpsLat && !gpsLng) {
      newErrors.gpsLng = 'Longitude required if latitude is provided';
    }

    if (gpsLng && !gpsLat) {
      newErrors.gpsLat = 'Latitude required if longitude is provided';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      newToast('error', 'Please fix the form errors');
      return;
    }

    setSaving(true);
    try {
      const payload: any = {
        site_id: siteId,
        checkpoint_name: checkpointName.trim(),
        description: description.trim() || undefined,
        location_description: locationDescription.trim() || undefined,
        validation_radius_m: validationRadius,
        status,
      };

      if (gpsLat && gpsLng) {
        payload.expected_gps_lat = Number(gpsLat);
        payload.expected_gps_lng = Number(gpsLng);
      }

      const response = await ApiClient.post('/create-checkpoint', payload);

      if (response.data.status === 'success') {
        newToast('success', 'Checkpoint created successfully');
        navigate('/account/checkpoints');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setSaving(false);
  };

  const handleCancel = () => {
    navigate('/account/checkpoints');
  };

  const handleGetCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        position => {
          setGpsLat(position.coords.latitude.toFixed(6));
          setGpsLng(position.coords.longitude.toFixed(6));
          newToast('success', 'Location captured successfully');
        },
        error => {
          newToast('error', `Failed to get current location: ${error.message}`);
        }
      );
    } else {
      newToast('error', 'Geolocation is not supported by your browser');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner label="Loading..." />
      </div>
    );
  }

  return (
    <PermissionGate privilege="checkpoints.create" showDenied>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={handleCancel} />
          <Text className={styles.pageTitle}>Add Checkpoint</Text>
        </div>

        <ContentCard>
          <div className={styles.infoBox}>
            <Text size={300} weight="semibold" style={{ display: 'block', marginBottom: '4px' }}>
              ℹ️ Checkpoint Information
            </Text>
            <Text size={200} style={{ color: '#323130' }}>
              Checkpoints are physical locations where guards scan QR codes during patrols. GPS
              coordinates enable location validation.
            </Text>
          </div>

          <form onSubmit={handleSubmit}>
            <div className={styles.formGrid}>
              <Field label="Zone">
                <SearchableDropdown
                  placeholder="All zones"
                  selectedValue={selectedZoneId}
                  onSelect={value => {
                    setSelectedZoneId(value);
                  }}
                  options={zones.map(zone => ({
                    id: zone.id.toString(),
                    label: zone.name || zone.zone_name || '',
                  }))}
                  clearable
                />
              </Field>

              <Field label="Client">
                <SearchableDropdown
                  placeholder="All clients"
                  selectedValue={selectedClientId}
                  onSelect={value => {
                    setSelectedClientId(value);
                  }}
                  options={clients.map(client => ({
                    id: client.id.toString(),
                    label: client.client_name,
                  }))}
                  clearable
                />
              </Field>

              <Field
                label="Site"
                required
                validationMessage={errors.siteId}
                validationState={errors.siteId ? 'error' : undefined}
              >
                <SearchableDropdown
                  placeholder="Select site"
                  selectedValue={siteId ? siteId.toString() : ''}
                  onSelect={value => {
                    setSiteId(Number(value));
                    setErrors({ ...errors, siteId: '' });
                  }}
                  options={filteredSites.map(site => ({
                    id: site.id.toString(),
                    label: site.name,
                  }))}
                  required
                />
              </Field>

              <Field
                label="Checkpoint Name"
                required
                validationMessage={errors.checkpointName}
                validationState={errors.checkpointName ? 'error' : undefined}
              >
                <Input
                  value={checkpointName}
                  onChange={e => {
                    setCheckpointName(e.target.value);
                    setErrors({ ...errors, checkpointName: '' });
                  }}
                  placeholder="e.g., Main Entrance"
                />
              </Field>

              <Field label="Description" className={styles.fullWidth}>
                <Textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Brief description of the checkpoint"
                  rows={3}
                />
              </Field>

              <Field label="Location Description" className={styles.fullWidth}>
                <Input
                  value={locationDescription}
                  onChange={e => setLocationDescription(e.target.value)}
                  placeholder="e.g., Ground floor, main lobby"
                />
              </Field>

              <div className={styles.fullWidth}>
                <Label>GPS Coordinates (Optional)</Label>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}
                >
                  <div style={{ flex: 1 }}>
                    <Field
                      label="Latitude"
                      validationMessage={errors.gpsLat}
                      validationState={errors.gpsLat ? 'error' : undefined}
                    >
                      <Input
                        value={gpsLat}
                        onChange={e => {
                          setGpsLat(e.target.value);
                          setErrors({ ...errors, gpsLat: '', gpsLng: '' });
                        }}
                        placeholder="e.g., 5.6037"
                        type="text"
                      />
                    </Field>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Field
                      label="Longitude"
                      validationMessage={errors.gpsLng}
                      validationState={errors.gpsLng ? 'error' : undefined}
                    >
                      <Input
                        value={gpsLng}
                        onChange={e => {
                          setGpsLng(e.target.value);
                          setErrors({ ...errors, gpsLat: '', gpsLng: '' });
                        }}
                        placeholder="e.g., -0.1870"
                        type="text"
                      />
                    </Field>
                  </div>
                  <Button
                    appearance="secondary"
                    icon={<LocationRegular />}
                    onClick={handleGetCurrentLocation}
                    style={{ marginTop: '22px' }}
                  >
                    Get Current Location
                  </Button>
                </div>
              </div>

              <Field label="Validation Radius (meters)">
                <div style={{ padding: '16px 8px' }}>
                  <Slider
                    min={5}
                    max={500}
                    value={validationRadius}
                    onChange={(_, data) => setValidationRadius(data.value)}
                  />
                  <Text size={200} style={{ color: '#605e5c', marginTop: '8px' }}>
                    {validationRadius}m - Guards must be within this distance to scan
                  </Text>
                </div>
              </Field>

              <Field label="Status">
                <Dropdown
                  value={status}
                  onOptionSelect={(_, data) => setStatus(data.optionValue as 'active' | 'inactive')}
                >
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Dropdown>
              </Field>
            </div>

            <div className={styles.actions}>
              <Button appearance="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button appearance="primary" icon={<SaveRegular />} type="submit" disabled={saving}>
                {saving ? 'Creating...' : 'Create Checkpoint'}
              </Button>
            </div>
          </form>
        </ContentCard>
      </div>
    </PermissionGate>
  );
};

export default AddCheckpointFluentUI;
